import React, { useState, useEffect } from "react";
import Select from 'react-select';
import * as Yup from "yup";
import { useNavigate } from "react-router";
import api from "../../services/api";
import { formatDateWithTime } from "../../utils/dateFormat";
import NoDataFound from "../../shared/NoDataFound";
import { useSelector } from "react-redux";

import Paginator from "../../shared/Paginator";
import config from "../../config";
import TableLoader from "../../shared/TableLoader";
import {
  roles,
  positionalPriceReferences,
  LocalPricePackages,
  pricePackages,
  positionalRageOptions,
} from "../../utils/constants";
import { debounce } from "../../utils/Debounce";
import {
  SlInput,
  SlIcon,
  SlOption,
  SlSelect,
  SlButton
} from "@shoelace-style/shoelace/dist/react";
import { Toast } from "../../shared/Toast";

import { isPlainObject } from "../MakerChecker/payload/constants";

/*
 * This screen shows the SUBMITTED (frontend-shaped) fields: lang_name, state_name,
 * price, package_weeks... None of those are columns on the price tables.
 *
 * v1 rows keep the submitted body on request_payload PERMANENTLY, while updated_payload
 * holds the frontend body only until approval and is then replaced by the post-approve DB
 * record (amount/dist_id, no *_name). So request_payload must win when present, otherwise
 * every approved v1 row would render "-". Legacy rows have no request_payload and fall
 * back to updated_payload exactly as before.
 */
const submittedFields = (item) =>
  (isPlainObject(item?.request_payload) ? item.request_payload : item?.updated_payload) || {};

/*
 * The "old price" column. Legacy rows stored the previous frontend body in payload
 * (key: price); v1 rows store the previous DB record keyed by table (column: amount).
 */
const previousPrice = (item) => {
  const payload = item?.payload;
  if (!isPlainObject(payload)) return null;
  if (payload.price != null) return payload.price; // legacy: flat frontend body
  const record = payload[item?.ref_table_name];
  if (isPlainObject(record)) return record.amount ?? record.price ?? null; // v1: keyed DB record
  return null;
};

const money = (v) => (v != null && v !== "" ? `₹${v}` : "-");

const STATUS_TEXT = { 0: "Pending", 1: "Approved", 2: "Rejected" };
const STATUS_CLS = {
  0: "bg-amber-100 text-amber-800",
  1: "bg-emerald-100 text-emerald-800",
  2: "bg-red-100 text-red-800",
};

function CardListMakerCheckerList() {

  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [tableReferenceFilter, setTableReferenceFilter] = useState(null)
  const [actionFilter, setActionFilter] = useState(null)
  const [statusFilter, setStatusFilter] = useState(null)
  const [creativeTypeFilter, setCreativeTypeFilter] = useState(null)
  const [weeksFilter, setWeeksFilter] = useState(null)
  const [positionFilter, setPositionFilter] = useState(null)
  const [loader, setLoader] = useState(false);
  const [selectedIds, setSelectedIds] = useState([])

  const [search, setSearch] = useState("");


  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const HeaderSearch = useSelector((state) => state.Head.adssearch);

  const handlePageClick = (event) => {
    setCurrentPage(event.selected);
    loadData(search, event.selected);
  };


  const loadData = async (searchString = "", page = 0) => {
    try {
      setLoader(true);
      let res = await api({
        url: `/makerchecker/getMakerCheckCardList`,
        method: "POST",
        responseType: "json",
        data: {
          page,
          limit: config.PER_PAGE,
          search: searchString,
          tableReferenceFilter: tableReferenceFilter ? tableReferenceFilter.value : undefined,
          actionFilter: actionFilter ? actionFilter.value : undefined,
          creativeTypeFilter: creativeTypeFilter ? creativeTypeFilter.value : undefined,
          weeksFilter: weeksFilter ? weeksFilter.value : undefined,
          positionFilter: positionFilter ? positionFilter.value : undefined,
          statusFilter: statusFilter !== null ? statusFilter?.value : undefined
        },
      });
      setData(res.data.data.length ? res.data.data : []);
      setSelectedIds([])
      setTotalPages(Math.ceil(res.data.total / config.PER_PAGE));
      setLoader(false);
    } catch (error) {
      setLoader(false);
      if (error?.response?.status === 401) navigate("/");
    }
  };

  const ApproveRejectMcActions = async(mc_ids, moveto) => {
    try {
      let res = await api({
        url: `/makerchecker/approveRejectMcActions`,
        method: "POST",
        responseType: "json",
        data: {
          mc_ids,
          moveto
        },
      });
      if(res.code === 200){
        Toast('success',"Request Approved Successfully")
        loadData()
        setSelectedIds([])
      }else {
          Toast('error',res?.data?.message)
      }
    } catch (error) {
      console.error("Error @ ApproveRejectMcActions", error)
    }
  }

  const searchData = (e) => {
    setSearch(e.target.value);
    setCurrentPage(0);
    loadData(e.target.value, 0);
  };

  useEffect(() => {
    Header();
  }, [HeaderSearch]);

  useEffect(() => {
    loadData()
  }, [HeaderSearch, tableReferenceFilter, actionFilter, creativeTypeFilter, weeksFilter, positionFilter, statusFilter]);

  const Header = () => {
    setSearch(HeaderSearch);
    setCurrentPage(0);
    loadData(HeaderSearch, 0);
  };


  return (
    <>
      <div className="flex items-center mb-3">
        <aside className="flex items-center gap-3 ml-auto flex-wrap w-full">
          <h5 className="mr-auto">Maker Checker Requests</h5>
          <div className="d-flex gap-1 items-center jus">
            <Select onChange={(selected) => { setTableReferenceFilter(selected) }} size="small" placeholder="Team" value={tableReferenceFilter} options={positionalPriceReferences} isClearable className='w-40' />
            <Select onChange={(selected) => { setActionFilter(selected) }} size="small" placeholder="Action" value={actionFilter}
              options={[{ label: "Add", value: 1 }, { label: "Edit", value: 2 }]} isClearable className='w-40' />
              <Select onChange={(selected) => { setStatusFilter(selected) }} size="small" placeholder="Status" value={statusFilter}
              options={[{ label: "Pending", value: 0 }, { label: "Approved", value: 1 }, { label: "Rejected", value: 2 }]} isClearable className='w-40' />
            <Select onChange={(selected) => { setCreativeTypeFilter(selected) }} size="small" placeholder="Creative Type" value={creativeTypeFilter} options={[{ label: "Full Page", value: "Full Page" }, { label: "Full Page", value: "Full Page Video" }, { label: "Promoted Story", value: "Promoted Story" }, { label: "Promoted Story", value: "Promoted Story" }, { label: "Square Classified", value: "square_classified" }, { label: "Rectangle Classified", value: "rectangle_classified" }]} isClearable className='w-40' />
            <Select onChange={(selected) => { setWeeksFilter(selected) }} size="small" placeholder="Weeks" value={weeksFilter}
              options={[...LocalPricePackages, ...pricePackages]} isClearable className='w-40' />
            <Select onChange={(selected) => { setPositionFilter(selected) }} size="small" placeholder="Position" value={positionFilter} options={[...positionalRageOptions]} isClearable className='w-40' />
            <SlInput
              size="small"
              id="myInput"
              onSlInput={debounce(searchData, 500)}
              placeholder="Search By Location"
            >
              <SlIcon library="lucide" name="map-pin" slot="prefix"></SlIcon>
            </SlInput>
           {selectedIds?.length ? <>
            <SlButton variant="success" onClick={() => {ApproveRejectMcActions(selectedIds, 1)}}>Approve</SlButton>
            <SlButton variant="danger" onClick={() => {ApproveRejectMcActions(selectedIds, 0)}}>Reject</SlButton>
            </> : null}
          </div>
          <div>
          </div>
        </aside>
      </div>

      {/* ---- mobile (<md): stacked cards. A 17-column table cannot be read on a phone,
             and approve/reject is a decision that must be legible. ---- */}
      <div className="md:hidden mb-4 space-y-3">
        {loader ? (
          [...Array(3)].map((_, i) => (
            <div key={i} className="border border-gray-200 rounded-lg p-3 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-2" />
              <div className="h-3 bg-gray-100 rounded w-1/3" />
            </div>
          ))
        ) : !data?.length ? (
          <div className="text-center py-8 border border-dashed border-gray-200 rounded-lg text-sm text-gray-500">
            No maker-checker requests found.
          </div>
        ) : (
          data.map((item) => {
            const f = submittedFields(item);
            const canAct = localStorage.getItem("role") === roles.financer && item.status === 0;
            const place = [f?.village_name, f?.mandal_name, f?.district_name, f?.state_name]
              .filter(Boolean).join(", ");
            return (
              <div key={item.mc_id} className="border border-gray-200 rounded-lg p-3">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-800 text-sm break-words">
                      {positionalPriceReferences?.find((i) => i.value === item?.ref_table_name)?.label || item?.ref_table_name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {item.action_type === 1 ? "Add" : item.action_type === 2 ? "Edit" : "-"}
                      {f?.lang_name ? ` · ${f.lang_name}` : ""}
                    </p>
                  </div>
                  <span className={`shrink-0 px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_CLS[item.status] || "bg-gray-100 text-gray-700"}`}>
                    {STATUS_TEXT[item.status] ?? "Unknown"}
                  </span>
                </div>

                <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-gray-600">
                  {place ? (
                    <div className="col-span-2 flex gap-1"><dt className="text-gray-400">Location:</dt><dd className="break-words">{place}</dd></div>
                  ) : null}
                  {f?.zone_name ? <div className="flex gap-1"><dt className="text-gray-400">Zone:</dt><dd>{f.zone_name}</dd></div> : null}
                  {f?.creative_type ? <div className="flex gap-1"><dt className="text-gray-400">Creative:</dt><dd className="break-words">{f.creative_type}</dd></div> : null}
                  {f?.positional_range ? <div className="flex gap-1"><dt className="text-gray-400">Position:</dt><dd>{f.positional_range}</dd></div> : null}
                  {f?.package_weeks ? <div className="flex gap-1"><dt className="text-gray-400">Weeks:</dt><dd>{f.package_weeks}</dd></div> : null}
                  <div className="col-span-2 flex items-center gap-2 pt-1">
                    <dt className="text-gray-400">Price:</dt>
                    <dd className="flex items-center gap-2">
                      <span className="text-gray-500 line-through">{money(previousPrice(item))}</span>
                      <span aria-hidden="true" className="text-gray-300">→</span>
                      <span className="text-emerald-700 font-semibold">{money(f?.price)}</span>
                    </dd>
                  </div>
                </dl>

                <p className="text-xs text-gray-400 mt-2">{formatDateWithTime(item?.time) || "-"}</p>

                {canAct && (
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => ApproveRejectMcActions([item.mc_id], 1)}
                      className="flex-1 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => ApproveRejectMcActions([item.mc_id], 0)}
                      className="flex-1 px-3 py-2 bg-white border border-red-300 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium"
                    >
                      Reject
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* ---- md+: the full table ----
           `max-md:hidden` rather than `hidden md:block`: global CSS defines
           `.hidden{display:none!important}`, which `.md\:block` cannot override, so
           `hidden md:block` would hide this table at every width. */}
      <div className="max-md:hidden overflow-x-auto mb-4">
        <table className="table table-bordered">
          <thead className="sticky top-0">
            <tr>{/*Select all checkboxes */}
              <th>{localStorage.getItem("role") === roles.financer ? <input type='checkbox' checked={data?.filter(
                item => item.status === 0)?.length > 0 && data?.filter(item => item.status === 0)?.every(item => selectedIds.includes(item.mc_id)) ? true : false} onChange={(e) => {
                if(e.target.checked){
                  setSelectedIds(data?.filter(item => item.status === 0)?.map(item => item.mc_id))
                }
                else{
                  setSelectedIds([])
                }
              }} /> : null}</th>
              <th>Team</th>
              <th>Request to</th>
              <th>Status</th>
              <th>Language</th>
              <th>Zone</th>
              <th>State</th>
              <th>District</th>
              <th>Constituency</th>
              <th>Mandal</th>
              <th>Village</th>
              <th>Creative Type</th>
              <th>Position</th>
              <th>Package</th>
              <th>Package Type</th>
              <th>Previous Amount</th>
              <th>Updated Amount</th>
              <th>Requested on</th>
              {localStorage.getItem("role") === roles.financer ? (
                <th>Actions</th>
              ) : null}
            </tr>
          </thead>{console.log(selectedIds)}
          {data?.length && !loader ? (
            <tbody>
              {data.map((item) => {
                return (
                  <tr key={item.mc_id}>
                    <th onClick={() => {
                      if(!(localStorage.getItem("role") === roles.financer && item.status === 0)) return;
                      if (selectedIds.includes(item.mc_id)) {
                        setSelectedIds(selectedIds.filter(id => id !== item.mc_id))
                      }
                      else {
                        setSelectedIds([...new Set([...selectedIds, item.mc_id])])
                      }
                    }}>
                      {localStorage.getItem("role") === roles.financer && item.status === 0 ? <input type='checkbox' checked={selectedIds.includes(item.mc_id)} /> : null}
                    </th>
                    <th>{positionalPriceReferences?.find(i => i.value === item?.ref_table_name)?.label}</th>
                    <th>{item.action_type === 1 ? "Add" : item.action_type === 2 ? 'Edit' : '-'}</th>
                    <th>{item.status === 0 ? 'Pending': item.status === 1 ?'Approved': item.status === 2 ? 'Rejected' : 'Unknown'}</th>
                    <td>{submittedFields(item)?.lang_name || "-"}</td>
                    <td>{submittedFields(item)?.zone_name || "-"}</td>
                    <td>{submittedFields(item)?.state_name || "-"}</td>
                    <td>{submittedFields(item)?.district_name || "-"}</td>
                    <td>{submittedFields(item)?.const_name || "-"}</td>
                    <td>{submittedFields(item)?.mandal_name || "-"}</td>
                    <td>{submittedFields(item)?.village_name || "-"}</td>
                    <td>{submittedFields(item)?.creative_type || "-"}</td>
                    <td>{submittedFields(item)?.positional_range || "-"}</td>
                    <td>{submittedFields(item)?.package_weeks ? `${submittedFields(item).package_weeks} ${submittedFields(item).package_weeks > 1 ? 'Weeks' : 'Week'}` : '-'}</td>
                    <td>{submittedFields(item)?.package_type || "-"}</td>
                    <td>{money(previousPrice(item))}</td>
                    <td>{money(submittedFields(item)?.price)}</td>
                    <td> {formatDateWithTime(item?.time) || '-'}</td>
                    {localStorage.getItem("role") === roles.financer && item.status === 0 ? (
                      <td>
                        <sl-tooltip content="Approve">
                          <sl-icon-button
                              library="lucide"
                              name="check-circle"
                              onClick={() => {ApproveRejectMcActions([item.mc_id], 1)}}
                            ></sl-icon-button>
                        </sl-tooltip>
                        <sl-tooltip content="Reject"><sl-icon-button
                              library="lucide"
                              name="x-circle"
                              onClick={() => {ApproveRejectMcActions([item.mc_id], 0)}}
                            ></sl-icon-button>
                        </sl-tooltip>
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          ) : !data?.length && !loader ? (
            <NoDataFound colspan="8" />
          ) : (
            <TableLoader colspan="8" />
          )}
        </table>
      </div>
      <div className="flex justify-end">
        {totalPages > 1 && (
          <Paginator
            pageCount={totalPages}
            handlePageClick={handlePageClick}
            forcePage={currentPage}
          />
        )}
      </div>
    </>
  );
}

export default CardListMakerCheckerList